import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const ServicesModuleEditor = ({ data, onChange, siteSlug }) => {
  const services = data.services || [];
  const layout = data.layout || 'grid';
  const [editingIdx, setEditingIdx] = useState(null);
  
  const addService = () => {
    const newService = { 
      id: Date.now().toString(),
      title: 'New Service', 
      description: '', 
      image: '', 
      price: '',
      duration: '',
      link: ''
    };
    onChange({...data, services: [...services, newService]});
    setEditingIdx(services.length);
  };
  
  const updateService = (idx, updates) => {
    const updated = [...services];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, services: updated});
  };
  
  const removeService = (idx) => {
    onChange({...data, services: services.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const renderEditor = (item, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Service</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="space-y-2">
        <Label>Service Title</Label>
        <Input value={item.title} onChange={(e) => updateService(idx, { title: e.target.value })} placeholder="e.g. Private Vocal Coaching" />
      </div>
      
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={item.description} onChange={(e) => updateService(idx, { description: e.target.value })} placeholder="Describe what's included..." rows={3} />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Price (optional)</Label>
          <Input value={item.price || ''} onChange={(e) => updateService(idx, { price: e.target.value })} placeholder="e.g. £75/hour" />
        </div>
        <div className="space-y-2">
          <Label>Duration (optional)</Label>
          <Input value={item.duration || ''} onChange={(e) => updateService(idx, { duration: e.target.value })} placeholder="e.g. 60 mins" />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Image</Label>
        <CloudinaryUploader
          currentImage={item.image}
          onUpload={(url) => updateService(idx, { image: url })}
          onRemove={() => updateService(idx, { image: '' })}
          folder={`sites/${siteSlug}/services`}
          aspectRatio="aspect-video"
          placeholder="Drop service image"
        />
      </div>
      
      <div className="space-y-2">
        <Label>Booking Link (optional)</Label>
        <Input value={item.link || ''} onChange={(e) => updateService(idx, { link: e.target.value })} placeholder="https://..." />
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeService(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Delete Service
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input value={data.title || 'Services'} onChange={(e) => onChange({...data, title: e.target.value})} />
      </div>

      <div className="space-y-3">
        <Label>Display Layout</Label>
        <div className="flex items-center gap-2">
          {['list', 'grid', 'cards'].map(l => (
            <button
              key={l}
              onClick={() => onChange({...data, layout: l})}
              className={cn(
                "flex flex-col items-center gap-1 px-4 py-3 rounded-lg border transition-all capitalize",
                layout === l ? "bg-orange-500/20 border-orange-500 text-orange-400" : "bg-slate-700/50 border-slate-600 text-slate-400 hover:border-slate-500"
              )}
            >
              <span className="text-xs">{l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Services ({services.length})</Label>
          <Button variant="outline" size="sm" onClick={addService}><Plus className="w-4 h-4 mr-1" /> Add Service</Button>
        </div>
        
        {services.length === 0 ? (
          <div className="text-center py-12 bg-slate-700/30 rounded-lg border border-slate-600">
            <Briefcase className="w-10 h-10 mx-auto text-slate-500 mb-3" />
            <p className="text-sm text-slate-400">No services yet. Add what you offer!</p>
          </div>
        ) : editingIdx !== null && services[editingIdx] ? (
          renderEditor(services[editingIdx], editingIdx)
        ) : (
          <div className="space-y-2">
            {services.map((item, idx) => (
              <div key={item.id || idx} onClick={() => setEditingIdx(idx)} className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group">
                <div className="w-16 h-12 rounded bg-slate-600 overflow-hidden flex-shrink-0">
                  {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Briefcase className="w-5 h-5 text-slate-500" /></div>}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{item.title || 'Untitled'}</h4>
                  <p className="text-xs text-slate-400">{item.price || 'Price not set'}{item.duration ? ` • ${item.duration}` : ''}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeService(idx); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Team Module Editor

